import { Prisma, Tag } from '@prisma/client';
import { json, LoaderFunction, MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { Container } from '~/components/Container';
import { Layout } from '~/components/Layout';
import { PageSubHeading } from '~/components/PageSubHeading';
import { Pedal, PedalList } from '~/components/PedalList';
import { ReadMoreButton } from '~/components/ReadMoreButton';
import { db } from '~/lib/db.server';
import { getCacheControlHeader } from '~/lib/http.server';
import { getImagePublicUrl } from '~/lib/supabase.server';
import { arrayToObjectById } from '~/lib/utils/array';
import { appendMetaTitleSuffix } from '~/lib/utils/meta';

interface TagWithPedalId {
  id: string;
  tagId: string;
  tag: string;
}

export const loader: LoaderFunction = async ({ request, params }) => {
  const { brandId } = params;

  const brand = await db.brand.findUnique({
    where: {
      id: brandId,
    },
  });

  if (!brand) {
    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });
  }

  // タグごとに3件ずつペダルを取得
  const tagWithPedalIdsData = await db.$queryRaw<TagWithPedalId[]>(Prisma.sql`
      SELECT sub.id, sub.tag, sub."tagId" FROM (
        SELECT
          p.id, t.tag, t.id AS "tagId", ROW_NUMBER() OVER (PARTITION BY t.id ORDER BY p.created_at DESC) as row_num
        FROM
          pedals AS p
          JOIN pedal_tags AS pt ON p.id = pt.pedal_id
          JOIN tags AS t ON pt.tag_id = t.id
        WHERE
          p.brand_id = ${brandId}
      ) as sub
      WHERE sub.row_num <= 3;
    `);

  // ペダルを取得
  const pedalIds = tagWithPedalIdsData.map(tagWithPedalId => tagWithPedalId.id);
  const pedalsQuery = db.pedal.findMany({
    take: 100,
    where: {
      id: {
        in: pedalIds
      },
    },
    include: {
      brand: true,
      images: true,
      tags: {
        include: {
          tag: true,
        },
      },
    },
    orderBy: {
      createdAt: 'desc',
    },
  });

  // タグを取得
  const tagIds = tagWithPedalIdsData.map(tagWithPedalId => tagWithPedalId.tagId);
  const uniqueTagIds = Array.from((new Set(tagIds)));
  const tagsQuery = db.tag.findMany({
    where: {
      id: {
        in: uniqueTagIds,
      }
    },
    orderBy: {
      order: 'asc',
    },
  });

  const [pedals, tags] = await Promise.all([
    pedalsQuery, tagsQuery
  ])

  // ペダルIDをキーにしたオブジェクトに変換
  const pedalsMap = arrayToObjectById('id', pedals);

  // タグ配下にペダル一覧を格納
  const filteredPedals = tags.map((tag) => {
    const tagByIds = tagWithPedalIdsData.filter((data) => data.tagId === tag.id).map(data => data.id);
    const items = tagByIds.map((id) => {
      return pedalsMap[id]
    });

    return {
      tag: tag,
      items: items,
    }
  });

  return json(
    {
      brand,
      filteredPedals,
      imageUrlPrefix: getImagePublicUrl(request),
    },
    {
      headers: {
        ...getCacheControlHeader()
      }
    }
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const brand = data.brand;
  return [
    { title: appendMetaTitleSuffix(`${brand.name}のエフェクター`) },
    { name: 'description', content: `${brand.description}` },
  ];
};

const BrandRoute = () => {
  const { brand, filteredPedals, imageUrlPrefix } = useLoaderData<typeof loader>();

  return (
    <>
      <Layout>
        <div className={'bg-slate-800 text-white p-6 md:p-8'}>

          <div className={'mb-8 md:mb-8 mt-4 bg-slate-700 inline-block rounded-md'}>
            <h1 className="text-3xl mx-6 my-3 font-semibold tracking-widest">{brand.name}</h1>
          </div>

          <div className="break-words whitespace-pre-wrap mb-4">
            <p className="leading-6 text-[14px] text-slate-200">{brand.description}</p>
          </div>
        </div>

        <Container>
          {filteredPedals.length > 0 && (
            <div>
              {filteredPedals.map((pedal: { tag: Tag; items: Pedal[] }) => (
                <div key={pedal.tag.id} className={'mt-5 mb-14'}>
                  <PageSubHeading>
                    #{pedal.tag.tag}
                  </PageSubHeading>

                  <div className={'grid lg:grid-cols-3 md:grid-cols-1 gap-6 mt-3'}>
                    <PedalList pedals={pedal.items} imageUrlPrefix={imageUrlPrefix} />
                  </div>

                  <div className='mt-9'>
                    <ReadMoreButton
                      to={`/pedals?brands=${brand.id}&tags=${pedal.tag.id}`}
                      label={`他の${pedal.tag.tag}も見てみる`}
                    />
                  </div>

                </div>
              ))}
            </div>
          )}

        </Container>
      </Layout>
    </>
  );
};

export default BrandRoute;
